import { Link } from 'react-router-dom';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { format, isToday, isTomorrow, isPast, parseISO } from 'date-fns';
import clsx from 'clsx';

interface Deadline {
  id: string;
  title: string;
  category: string;
  dueDate: string;
  status: string;
  isOverdue: boolean;
}

interface UpcomingDeadlinesProps {
  deadlines: Deadline[];
}

function formatDueDate(dateStr: string): string {
  const date = parseISO(dateStr);
  if (isToday(date)) return 'Today';
  if (isTomorrow(date)) return 'Tomorrow';
  return format(date, 'MMM d');
}

const categoryColors: Record<string, string> = {
  REGULATORY: 'bg-blue-100 text-blue-700',
  HMDA: 'bg-purple-100 text-purple-700',
  FAIR_LENDING: 'bg-teal-100 text-teal-700',
  MARKETING: 'bg-orange-100 text-orange-700',
  EXAM: 'bg-red-100 text-red-700',
};

function getCategoryBadgeClass(category: string): string {
  return categoryColors[category] || 'bg-gray-100 text-gray-700';
}

function formatCategory(category: string): string {
  return category.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function UpcomingDeadlines({ deadlines }: UpcomingDeadlinesProps) {
  if (!deadlines || deadlines.length === 0) {
    return (
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Deadlines</h3>
        <div className="flex flex-col items-center justify-center py-8 text-gray-400">
          <CalendarDaysIcon className="h-10 w-10 mb-2" />
          <p className="text-sm text-gray-500">No upcoming deadlines</p>
        </div>
      </div>
    );
  }

  const displayDeadlines = deadlines.slice(0, 7);

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Deadlines</h3>
      <div className="space-y-3">
        {displayDeadlines.map((deadline) => {
          const overdue = deadline.isOverdue || isPast(parseISO(deadline.dueDate));

          return (
            <div
              key={deadline.id}
              className="flex items-center gap-3"
            >
              <CalendarDaysIcon
                className={clsx(
                  'h-5 w-5 flex-shrink-0',
                  overdue ? 'text-red-500' : 'text-gray-400'
                )}
              />
              <div className="min-w-0 flex-1">
                <p
                  className={clsx(
                    'text-sm font-medium truncate',
                    overdue ? 'text-red-700' : 'text-gray-900'
                  )}
                >
                  {deadline.title}
                </p>
                <span
                  className={clsx(
                    'inline-block text-xs px-1.5 py-0.5 rounded mt-0.5',
                    getCategoryBadgeClass(deadline.category)
                  )}
                >
                  {formatCategory(deadline.category)}
                </span>
              </div>
              <span
                className={clsx(
                  'text-sm whitespace-nowrap flex-shrink-0',
                  overdue ? 'font-semibold text-red-600' : 'text-gray-500'
                )}
              >
                {overdue ? 'Overdue' : formatDueDate(deadline.dueDate)}
              </span>
            </div>
          );
        })}
      </div>
      <div className="mt-4 pt-3 border-t border-gray-100">
        <Link
          to="/calendar"
          className="text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors"
        >
          View Calendar &rarr;
        </Link>
      </div>
    </div>
  );
}
